import React from "react";
import {
  X,
  FileText,
  Sheet,
  Presentation,
  FileType,
  Download,
} from "lucide-react";
import {
  getDocumentIcon,
  getDocumentColor,
  formatFileSize,
} from "../../../utils/documentCompression";

/**
 * Display grid of uploaded documents with remove buttons
 */
const DocumentGrid = ({ documents = [], onRemove }) => {
  if (!documents || documents.length === 0) {
    return null;
  }

  /**
   * Get icon component based on extension
   */
  const getIconComponent = (extension) => {
    const iconName = getDocumentIcon(extension);

    switch (iconName) {
      case "FileText":
        return FileText;
      case "Sheet":
        return Sheet;
      case "Presentation":
        return Presentation;
      case "FileType":
        return FileType;
      default:
        return FileText;
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">
        Uploaded Documents ({documents.length})
      </h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {documents.map((doc, index) => {
          const extension =
            doc.extension ||
            doc.name?.split(".").pop()?.toLowerCase() ||
            "file";
          const IconComponent = getIconComponent(extension);
          const colorClass = getDocumentColor(extension);

          return (
            <div
              key={index}
              className="
                relative group
                bg-white border border-gray-200 rounded-lg p-3
                hover:border-gray-300 hover:shadow-sm
                transition-all duration-200
              "
            >
              {/* Remove button */}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="
                  absolute -top-2 -right-2
                  w-6 h-6 flex items-center justify-center
                  bg-red-500 text-white rounded-full
                  hover:bg-red-600
                  opacity-0 group-hover:opacity-100
                  transition-opacity duration-200
                  z-10
                "
                aria-label="Remove document"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Document info */}
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className={`flex-shrink-0 ${colorClass}`}>
                  <IconComponent className="w-8 h-8" />
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  {/* File name */}
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {doc.name}
                  </p>

                  {/* File info */}
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <span className="uppercase font-medium">{extension}</span>
                    <span>•</span>
                    <span>
                      {formatFileSize(doc.size || doc.originalSize || 0)}
                    </span>
                    {doc.compressionRatio > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-green-600 font-medium">
                          {doc.compressionRatio}% compressed
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentGrid;
